import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InsightsPanel = ({ insights, recommendations, onApplyRecommendation }) => {
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [activeTab, setActiveTab] = useState('insights');

  const getInsightIcon = (type) => {
    switch (type) {
      case 'trend':
        return 'TrendingUp';
      case 'pattern':
        return 'BarChart3';
      case 'achievement':
        return 'Award';
      case 'warning':
        return 'AlertTriangle';
      case 'suggestion':
        return 'Lightbulb';
      default:
        return 'Info';
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'trend':
        return 'text-success';
      case 'pattern':
        return 'text-primary';
      case 'achievement':
        return 'text-accent';
      case 'warning':
        return 'text-error';
      case 'suggestion':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getInsightBgColor = (type) => {
    switch (type) {
      case 'trend':
        return 'bg-success/10 border-success/20';
      case 'pattern':
        return 'bg-primary/10 border-primary/20';
      case 'achievement':
        return 'bg-accent/10 border-accent/20';
      case 'warning':
        return 'bg-error/10 border-error/20';
      case 'suggestion':
        return 'bg-warning/10 border-warning/20';
      default:
        return 'bg-muted/10 border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground">Wellness Insights</h3>
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          <Button
            variant={activeTab === 'insights' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('insights')}
          >
            Insights
          </Button>
          <Button
            variant={activeTab === 'recommendations' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('recommendations')}
          >
            Recommendations
          </Button>
        </div>
      </div>
      {activeTab === 'insights' && (
        <div className="space-y-4">
          {insights?.map((insight) => (
            <div
              key={insight?.id}
              className={`p-4 rounded-lg border transition-all duration-300 cursor-pointer hover:shadow-wellness-md ${getInsightBgColor(insight?.type)}`}
              onClick={() => setSelectedInsight(insight)}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg ${getInsightBgColor(insight?.type)} flex items-center justify-center`}>
                  <Icon 
                    name={getInsightIcon(insight?.type)} 
                    size={20} 
                    className={getInsightColor(insight?.type)} 
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-base font-medium text-foreground">{insight?.title}</h4>
                    <span className="text-xs text-muted-foreground">{formatDate(insight?.date)}</span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{insight?.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {insight?.category && (
                        <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground capitalize">
                          {insight?.category}
                        </span>
                      )}
                      
                      {insight?.impact && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Zap" size={14} className="text-accent" />
                          <span className="text-xs text-accent-foreground">
                            {insight?.impact} impact
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'recommendations' && (
        <div className="space-y-4">
          {recommendations?.map((recommendation) => (
            <div
              key={recommendation?.id}
              className="p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-wellness"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Lightbulb" size={20} className="text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-medium text-foreground mb-1">{recommendation?.title}</h4>
                    <p className="text-sm text-muted-foreground">{recommendation?.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium capitalize ${getPriorityColor(recommendation?.priority)}`}>
                    {recommendation?.priority}
                  </span>
                  <Icon 
                    name="Flag" 
                    size={14} 
                    className={getPriorityColor(recommendation?.priority)} 
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {recommendation?.estimatedTime && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{recommendation?.estimatedTime}</span>
                    </div>
                  )}
                  
                  {recommendation?.difficulty && (
                    <div className="flex items-center space-x-1">
                      <Icon name="BarChart3" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground capitalize">{recommendation?.difficulty}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onApplyRecommendation(recommendation, 'dismiss')}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onApplyRecommendation(recommendation, 'apply')}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Insight Detail Modal */}
      {selectedInsight && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-lg shadow-wellness-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">Insight Details</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedInsight(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto rounded-lg ${getInsightBgColor(selectedInsight?.type)} 
                flex items-center justify-center mb-4`}>
                <Icon 
                  name={getInsightIcon(selectedInsight?.type)} 
                  size={24} 
                  className={getInsightColor(selectedInsight?.type)} 
                />
              </div>
              
              <h4 className="text-xl font-heading font-bold text-foreground mb-2">
                {selectedInsight?.title}
              </h4>
              
              <p className="text-muted-foreground mb-4">{selectedInsight?.description}</p>
            </div>
            
            {selectedInsight?.details && (
              <div className="bg-muted/30 rounded-lg p-4 mb-6">
                <h5 className="font-medium text-foreground mb-2">Detailed Analysis</h5>
                <p className="text-sm text-muted-foreground">{selectedInsight?.details}</p>
              </div>
            )}
            
            {selectedInsight?.actionItems && selectedInsight?.actionItems?.length > 0 && (
              <div className="mb-6">
                <h5 className="font-medium text-foreground mb-3">Suggested Actions</h5>
                <div className="space-y-2">
                  {selectedInsight?.actionItems?.map((action, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 bg-muted/30 rounded-lg">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-foreground">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setSelectedInsight(null)}
              >
                Close
              </Button>
              
              {selectedInsight?.actionable && (
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => {
                    onApplyRecommendation(selectedInsight, 'create-goal');
                    setSelectedInsight(null);
                  }}
                  iconName="Target"
                  iconPosition="left"
                >
                  Create Goal
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsPanel;